/**
 * R11 — Visitor at gate: full-screen approval takeover.
 * Redesign v3 "Visitor approval" frame: a fixed dark-green brand-chrome
 * surface (README: "--primary-700 panels use fixed dark-brand hexes
 * intentionally" — this card must read the same in light + dark app theme,
 * like a gate kiosk), arch-shaped photo frame, and a 30-second depleting
 * amber countdown bar (the sole amber accent on this screen).
 * Port of design/source/screens-visitor.jsx VisitorApprovalScreen.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Animated, Image, Pressable, ScrollView, Text as RNText, View, useWindowDimensions } from 'react-native';

import { palette, radius, space } from '@bero/tokens';
import { extractItems, fmtTime, num, str } from '@/components/gate/api';
import { Icon } from '@/components/gate/icons';
import { api } from '@/lib/client';
import { useT, type TFunc } from '@/i18n';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, EmptyState, Screen, Skeleton } from '@/ui';

type PendingEntry = {
  id: number; name: string; purpose: string; plate?: string; arrivedAt?: string; photo?: string; idType?: string;
};

const TYPE_LABEL: Record<string, string> = { VISITOR: 'Guest', DELIVERY: 'Delivery', CAB: 'Cab', STAFF: 'Staff' };

function toPending(raw: unknown): PendingEntry {
  const type = (str(raw, 'type') ?? 'VISITOR').toUpperCase();
  const notes = str(raw, 'notes');
  const company = str(raw, 'company');
  const purpose = (notes && notes.replace(/\s*-\s*awaiting approval$/i, '')) || company || TYPE_LABEL[type] || 'Visitor';
  return {
    id: num(raw, 'id') ?? 0,
    name: str(raw, 'name') ?? 'Visitor',
    purpose,
    plate: str(raw, 'vehicleNo'),
    arrivedAt: str(raw, 'arrivedAt', 'createdAt'),
    photo: str(raw, 'photo'),
    idType: str(raw, 'idType'),
  };
}

function formatFlat(code?: string): string {
  if (!code) return '—';
  return code.replace(/^([A-Za-z]+)(\d+)$/, '$1-$2');
}

const HOLD_MS = 30000; // design: countdown 30s linear keyframe

/** Fixed brand-green chrome (never swaps with theme — see bero-tokens.css
 * README note on --primary-700 solid panels). Sourced from the light
 * palette so this reads as "gate green" in both app themes. */
const BRAND = palette.light;
const BRAND_FIGURE = palette.dark.accent500; // bright amber pops on the dark surface either way
const BRAND_DENY_TEXT = palette.dark.danger700; // light-on-dark readable danger red

type Decision = 'approve' | 'reject' | 'gate';

/** Full lockup, cream-on-green — official logo with fixed on-dark colors
 * (brand rule: always the full logo, never the bare mark). */
function GateLockup({ height = 20 }: { height?: number }) {
  const word = Math.round(height * 0.76);
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: Math.max(6, Math.round(height * 0.19)) }}>
      <GateMark height={height} />
      <RNText style={{ fontFamily: 'Sora_800ExtraBold', fontSize: word, lineHeight: Math.round(word * 1.25), letterSpacing: -word * 0.04, color: BRAND.canvas0 }}>bero</RNText>
    </View>
  );
}

function GateMark({ height = 20 }: { height?: number }) {
  const u = height / 58;
  return (
    <View style={{ width: 50 * u, height }}>
      <View style={{
        position: 'absolute', left: 2 * u, top: 1 * u, width: 46 * u, height: 55 * u,
        borderWidth: 9 * u, borderBottomWidth: 0, borderColor: BRAND.canvas0,
        borderTopLeftRadius: 23 * u, borderTopRightRadius: 23 * u,
      }} />
      <View style={{ position: 'absolute', left: 19 * u, top: 24 * u, width: 12 * u, height: 12 * u, borderRadius: 6 * u, backgroundColor: BRAND_FIGURE }} />
      <View style={{ position: 'absolute', left: 20 * u, top: 40 * u, width: 10 * u, height: 16 * u, borderRadius: 5 * u, backgroundColor: BRAND_FIGURE }} />
    </View>
  );
}

/** Arch-shaped photo placeholder (design's signature avatar motif), sized up
 * for the takeover hero. */
function ArchPhoto({ name, photo, size = 132 }: { name: string; photo?: string; size?: number }) {
  const initials = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <View style={{
      width: size, height: size * 1.14,
      borderTopLeftRadius: size / 2, borderTopRightRadius: size / 2,
      borderBottomLeftRadius: size * 0.09, borderBottomRightRadius: size * 0.09,
      backgroundColor: BRAND.primary600, borderWidth: 3, borderColor: BRAND.primary400,
      alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
    }}>
      {photo ? (
        <Image source={{ uri: photo }} resizeMode="cover" style={{ width: '100%', height: '100%' }} />
      ) : (
        <RNText style={{ fontFamily: 'Sora_800ExtraBold', fontSize: size * 0.3, color: BRAND.primary100 }}>{initials}</RNText>
      )}
    </View>
  );
}

function TakeoverBody({ compact, timer, fill, decided, decide, name, purpose, flat, plate, arrivedAt, photo, idType, onClose, t }: {
  compact?: boolean; timer: string; fill: Animated.Value; decided: Decision | null;
  decide: (k: Decision) => void; name: string; purpose: string; flat: string; plate?: string;
  arrivedAt?: string; photo?: string; idType?: string; onClose: () => void; t: TFunc;
}) {
  const pad = compact ? space.s5 : 0;
  void pad;
  return (
    <>

      {/* header — brand chrome + gate/time context (replaces a separate TopBar) */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, paddingHorizontal: space.s5, paddingVertical: space.s4 }}>
        <GateLockup height={20} />
        <AppText variant="label" style={{ marginLeft: 'auto', color: BRAND.primary100 }}>{t('main_gate_now')}</AppText>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('close_wo_deciding')}
          onPress={onClose}
          hitSlop={8}
          style={({ pressed }) => ({
            width: 32, height: 32, borderRadius: radius.full, marginLeft: space.s3,
            alignItems: 'center', justifyContent: 'center',
            backgroundColor: pressed ? BRAND.primary600 : 'transparent',
            borderWidth: 1, borderColor: BRAND.primary400,
          })}
        >
          <Icon name="x" size={15} color={BRAND.canvas0} />
        </Pressable>
      </View>

      <View style={{ flex: compact ? undefined : 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: space.s6, paddingTop: compact ? space.s2 : 0 }}>
        <ArchPhoto name={name} photo={photo} size={compact ? 96 : 132} />
        <AppText variant={compact ? "headingLg" : "displayLg"} weight="700" style={{ color: BRAND.canvas0, marginTop: compact ? space.s3 : space.s5, textAlign: 'center' }}>{name}</AppText>
        <AppText variant="bodyLg" style={{ color: BRAND.primary100, marginTop: 6, textAlign: 'center' }}>{t('for_flat', { purpose, flat })}</AppText>

        <View style={{ marginTop: space.s4, flexDirection: 'row', gap: space.s2, flexWrap: 'wrap', justifyContent: 'center' }}>
          {photo ? (
            <View style={{ borderWidth: 1, borderColor: BRAND.primary400, borderRadius: radius.full, paddingHorizontal: space.s3, paddingVertical: 6 }}>
              <AppText variant="label" style={{ color: BRAND.primary100 }}>{t('photo_taken')}</AppText>
            </View>
          ) : null}
          {plate ? (
            <View style={{ borderWidth: 1, borderColor: BRAND.primary400, borderRadius: radius.full, paddingHorizontal: space.s3, paddingVertical: 6 }}>
              <AppText variant="bodySm" mono style={{ color: BRAND.primary100 }}>{plate}</AppText>
            </View>
          ) : null}
        </View>

        {/* verification context — REAL fields only (design-for-everyone rule:
            never fabricate trust signals like a fake "ID verified" badge). */}
        <View style={{ alignSelf: 'stretch', marginTop: space.s6, borderRadius: radius.lg, backgroundColor: BRAND.primary600, padding: space.s4, gap: space.s1 }}>
          {([[t('logged_by'), t('gate_desk')], [t('arrived_at'), fmtTime(arrivedAt ? new Date(arrivedAt) : new Date())]] as const).map(([k, v]) => (
            <View key={k} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3 }}>
              <AppText variant="bodySm" style={{ color: BRAND.primary100 }}>{k}</AppText>
              <AppText variant="bodySm" weight="500" style={{ color: BRAND.canvas0 }}>{v}</AppText>
            </View>
          ))}
          {idType ? (
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3 }}>
              <AppText variant="bodySm" style={{ color: BRAND.primary100 }}>{t('id_checked')}</AppText>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <Icon name="check-circle" size={14} color={BRAND.canvas0} />
                <AppText variant="bodySm" weight="500" style={{ color: BRAND.canvas0 }}>{idType}</AppText>
              </View>
            </View>
          ) : null}
        </View>

        {/* depleting countdown — the one amber accent on this screen */}
        <View style={{ alignSelf: 'stretch', marginTop: compact ? space.s5 : space.s10 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: space.s2 }}>
            <AppText variant="bodySm" weight="600" style={{ color: BRAND.primary100 }}>{t('auto_deny_in')}</AppText>
            <AppText variant="bodySm" weight="600" mono style={{ color: BRAND.primary100 }}>{timer}</AppText>
          </View>
          <View style={{ height: 6, borderRadius: 3, backgroundColor: BRAND.primary700, overflow: 'hidden' }}>
            <Animated.View style={{
              height: '100%', borderRadius: 3, backgroundColor: BRAND_FIGURE,
              width: fill.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }),
            }} />
          </View>
        </View>
      </View>

      <View style={{ paddingHorizontal: space.s5, paddingBottom: space.s8, gap: space.s3 }}>
        <Pressable
          accessibilityRole="button"
          disabled={!!decided}
          onPress={() => decide('approve')}
          style={({ pressed }) => ({
            minHeight: 56, borderRadius: radius.lg, alignItems: 'center', justifyContent: 'center',
            backgroundColor: pressed ? BRAND.primary100 : BRAND.canvas0,
            opacity: decided && decided !== 'approve' ? 0.4 : 1,
          })}
        >
          <AppText variant="bodyLg" weight="600" style={{ color: BRAND.primary700 }}>
            {decided === 'approve' ? t('allowed_notified') : t('allow_in')}
          </AppText>
        </Pressable>
        <View style={{ flexDirection: 'row', gap: space.s3 }}>
          <Pressable
            accessibilityRole="button"
            disabled={!!decided}
            onPress={() => decide('gate')}
            style={({ pressed }) => ({
              flex: 1, minHeight: 48, borderRadius: radius.md, borderWidth: 1.5, borderColor: BRAND.primary400,
              alignItems: 'center', justifyContent: 'center',
              backgroundColor: pressed ? BRAND.primary600 : 'transparent',
              opacity: decided && decided !== 'gate' ? 0.4 : 1,
            })}
          >
            <AppText variant="bodyMd" weight="600" style={{ color: BRAND.canvas0 }}>
              {decided === 'gate' ? t('kept_at_gate') : t('leave_at_gate')}
            </AppText>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            disabled={!!decided}
            onPress={() => decide('reject')}
            style={({ pressed }) => ({
              flex: 1, minHeight: 48, borderRadius: radius.md, borderWidth: 1.5, borderColor: palette.light.danger500,
              alignItems: 'center', justifyContent: 'center',
              backgroundColor: pressed ? BRAND.primary600 : 'transparent',
              opacity: decided && decided !== 'reject' ? 0.4 : 1,
            })}
          >
            <AppText variant="bodyMd" weight="600" style={{ color: BRAND_DENY_TEXT }}>
              {decided === 'reject' ? t('denied') : t('deny')}
            </AppText>
          </Pressable>
        </View>
      </View>
    </>
  );
}

export default function VisitorApprovalScreen() {
  const params = useLocalSearchParams<{ id?: string; name?: string; purpose?: string; meta?: string; plate?: string; flat?: string }>();
  const { user } = useSession();
  const { t } = useT();

  const [loadState, setLoadState] = React.useState<'loading' | 'ready' | 'empty' | 'error'>('loading');
  const [entry, setEntry] = React.useState<PendingEntry | null>(null);
  const [decideError, setDecideError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await api.get<unknown>('/api/visitors/pending');
        const list = extractItems<unknown>(body).map(toPending);
        if (cancelled) return;
        const wanted = params.id ? list.find((e) => String(e.id) === String(params.id)) : list[0];
        if (wanted) {
          setEntry(wanted);
          setLoadState('ready');
        } else {
          setLoadState('empty');
        }
      } catch {
        if (!cancelled) setLoadState('error');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const name = entry?.name ?? (params.name || '');
  const purpose = entry?.purpose ?? (params.purpose || '');
  const flat = formatFlat(user?.flat?.code) || params.flat || '—';
  const plate = entry?.plate ?? params.plate;

  const [secondsLeft, setSecondsLeft] = React.useState(30);
  const [decided, setDecided] = React.useState<Decision | null>(null);
  const [fill] = React.useState(() => new Animated.Value(1));

  const decide = React.useCallback((kind: Decision) => {
    if (!entry) return;
    setDecided((prev) => {
      if (prev) return prev;
      const action = kind === 'gate' ? 'leave_at_gate' : kind;
      setDecideError(null);
      void (async () => {
        try {
          await api.post<unknown>(`/api/visitors/${entry.id}/respond`, { action });
          setTimeout(() => { router.back(); }, 650);
        } catch {
          setDecided(null);
          setDecideError(t('decide_error'));
        }
      })();
      return kind;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, t]);

  React.useEffect(() => {
    if (loadState !== 'ready') return;
    Animated.timing(fill, { toValue: 0, duration: HOLD_MS, useNativeDriver: false }).start();
    const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [fill, loadState]);

  // 0:00 → the bar promised "Auto-deny in": honor it. Deferred a tick so the
  // decision doesn't set state synchronously inside the effect body.
  React.useEffect(() => {
    if (loadState !== 'ready' || secondsLeft !== 0) return;
    const t = setTimeout(() => decide('reject'), 0);
    return () => clearTimeout(t);
  }, [secondsLeft, decide, loadState]);

  const timer = `0:${String(secondsLeft).padStart(2, '0')}`;
  const { width } = useWindowDimensions();
  const { colors } = useTheme();
  const wide = width >= 900;

  if (loadState === 'loading') {
    return (
      <Screen scroll={false} safeTop style={{ backgroundColor: BRAND.primary700 }}>
        <View style={{ flex: 1, padding: space.s6, justifyContent: 'center' }}>
          <Skeleton h={132} r={66} />
          <Skeleton h={28} style={{ marginTop: space.s5 }} />
          <Skeleton h={120} style={{ marginTop: space.s5 }} />
        </View>
      </Screen>
    );
  }

  if (loadState === 'empty' || loadState === 'error') {
    return (
      <Screen scroll={false} safeTop>
        <EmptyState
          icon={<Icon name="check-circle" size={40} color={colors.textTertiary} />}
          title={loadState === 'error' ? t('couldnt_load_visitors') : t('no_one_waiting')}
          body={loadState === 'error' ? t('couldnt_load_visitors_body') : t('no_one_waiting_body')}
          cta={t('back_to_home')}
          onCta={() => router.back()}
        />
      </Screen>
    );
  }

  if (wide) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.scrim, alignItems: 'center', justifyContent: 'center', padding: space.s6 }}>
        <Pressable accessibilityLabel={t('close_wo_deciding')} onPress={() => router.back()} style={{ position: 'absolute', inset: 0 }} />
        <View style={{ width: 400, maxHeight: '92%', borderRadius: radius.xl, overflow: 'hidden', backgroundColor: BRAND.primary700 }}>
          <ScrollView showsVerticalScrollIndicator={false}>
            <TakeoverBody compact timer={timer} fill={fill} decided={decided} decide={decide}
              name={name} purpose={purpose} flat={flat} plate={plate} arrivedAt={entry?.arrivedAt} photo={entry?.photo} idType={entry?.idType} onClose={() => router.back()} t={t} />
            {decideError ? (
              <AppText variant="bodySm" style={{ color: BRAND_DENY_TEXT, textAlign: 'center', paddingBottom: space.s4, paddingHorizontal: space.s5 }}>{decideError}</AppText>
            ) : null}
          </ScrollView>
        </View>
      </View>
    );
  }

  return (
    <Screen scroll={false} safeTop style={{ backgroundColor: BRAND.primary700 }}>
      <TakeoverBody timer={timer} fill={fill} decided={decided} decide={decide}
        name={name} purpose={purpose} flat={flat} plate={plate} arrivedAt={entry?.arrivedAt} photo={entry?.photo} idType={entry?.idType} onClose={() => router.back()} t={t} />
      {decideError ? (
        <AppText variant="bodySm" style={{ color: BRAND_DENY_TEXT, textAlign: 'center', paddingHorizontal: space.s5, paddingBottom: space.s4 }}>{decideError}</AppText>
      ) : null}
    </Screen>
  );
}
